import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Check, CheckCheck, ExternalLink, Users, AlertTriangle, Mail, ArrowRightLeft } from 'lucide-react';
import axiosService from '../services/axiosService';

import { API_BASE_URL as API_BASE } from '../config/api';

const TYPE_CONFIG = {
    new_audit: { icon: Users, color: '#415069', bg: '#e2e8f0' },
    audit_done: { icon: Check, color: '#3bb77e', bg: '#dcfce7' },
    audit_batch_done: { icon: CheckCheck, color: '#2563eb', bg: '#dbeafe' },
    audit_error: { icon: AlertTriangle, color: '#ef4444', bg: '#fee2e2' },
    lead_converted: { icon: ArrowRightLeft, color: '#415069', bg: '#e2e8f0' },
    email_sent: { icon: Mail, color: '#2563eb', bg: '#dbeafe' },
    system: { icon: Bell, color: '#94a3b8', bg: '#f1f5f9' },
};

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return `Il y a ${Math.floor(diff / 86400)}j`;
}

export default function NotificationCenter() {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const panelRef = useRef(null);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    const fetchNotifications = useCallback(async () => {
        try {
            const res = await axiosService.get(`${API_BASE}/api/public/admin/notifications`, { params: { limit: 20 } });
            setNotifications(res.data.items || []);
            setUnreadCount(res.data.unread_count || 0);
        } catch (_) {}
    }, []);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            const inButton = panelRef.current?.contains(e.target);
            const inDropdown = dropdownRef.current?.contains(e.target);
            if (!inButton && !inDropdown) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const markRead = async (id) => {
        try {
            await axiosService.patch(`${API_BASE}/api/public/admin/notifications/${id}/read`);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (_) {}
    };

    const markAllRead = async () => {
        try {
            await axiosService.patch(`${API_BASE}/api/public/admin/notifications/read-all`);
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (_) {}
    };

    return (
        <div className="relative" ref={panelRef}>
            <button
                ref={buttonRef}
                onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg text-blue-gray-400 hover:text-accent hover:bg-accent/10 transition"
                aria-label="Notifications"
            >
                <Bell size={18} />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full bg-secondary text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed w-96 bg-white rounded-lg shadow-xl border border-blue-gray-100 overflow-hidden z-[99999]"
                    style={{
                        left: buttonRef.current ? buttonRef.current.getBoundingClientRect().right + 8 : 'auto',
                        top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom - 8 : 'auto',
                        transform: 'translateY(-100%)',
                    }}
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-blue-gray-100">
                        <h3 className="font-heading font-semibold text-blue-gray-800 text-sm">Notifications</h3>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllRead}
                                    className="text-xs text-accent hover:text-accent-dark font-medium flex items-center gap-1"
                                >
                                    <CheckCheck size={12} />
                                    Tout lire
                                </button>
                            )}
                            <button onClick={() => setOpen(false)} className="text-blue-gray-300 hover:text-blue-gray-500">
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="max-h-[400px] overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="py-12 text-center text-blue-gray-300 text-sm">Aucune notification</div>
                        ) : (
                            notifications.map(n => {
                                const config = TYPE_CONFIG[n.type] || TYPE_CONFIG.system;
                                const Icon = config.icon;
                                return (
                                    <div
                                        key={n.id}
                                        className={`flex items-start gap-3 px-4 py-3 border-b border-blue-gray-50 transition cursor-pointer hover:bg-blue-gray-50 ${!n.is_read ? 'bg-accent/[0.03]' : ''}`}
                                        onClick={() => !n.is_read && markRead(n.id)}
                                    >
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: config.bg }}>
                                            <Icon size={14} style={{ color: config.color }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-blue-gray-800 truncate">{n.title}</span>
                                                {!n.is_read && <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />}
                                            </div>
                                            {n.message && <p className="text-xs text-blue-gray-400 mt-0.5 truncate">{n.message}</p>}
                                            <span className="text-[10px] text-blue-gray-300 mt-1 block">{timeAgo(n.created_at)}</span>
                                        </div>
                                        {n.data?.token && (
                                            <a
                                                href={`/audit/${n.data.token}?utm_source=staff`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-gray-300 hover:text-accent flex-shrink-0 mt-1"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <ExternalLink size={12} />
                                            </a>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
